import { createAsyncThunk } from '@reduxjs/toolkit';
import localDB from 'lib/IndexedDB/RessourcesDB';

import { getLyrics } from 'services/networking/lyrics';
import { getSwipeColor } from 'services/networking/swipe';
import { Lyrics } from 'shared-types/lyrics';
import { PLAYER_TYPE, VideoMetadata } from 'shared-types/video-metadata';
import { SwipeColors } from 'types/swipeColors';

import { NotPremiumError } from './errors';
import {
  FetchColorPayload,
  FetchLyricsPayload,
  FetchVideoMetadataAndNextResourcesPayload,
} from './types';

export const VIDEO_PLAYER_RESOURCES_ACTION_PREFIX = 'videoPlayerResources';

export const fetchVideoMetadataAndNextResources = createAsyncThunk<
  VideoMetadata,
  FetchVideoMetadataAndNextResourcesPayload,
  { rejectValue: NotPremiumError }
>(
  `${VIDEO_PLAYER_RESOURCES_ACTION_PREFIX}/fetchVideoMetadata`,
  async (
    { getVideoMetadata, videoFilename, colorSetting, userId, isStorageActivated },
    thunkApi,
  ) => {
    try {
      const videoMetadata = await getVideoMetadata(videoFilename, userId);
      if (videoMetadata.playerType === PLAYER_TYPE.MP3_KBP) {
        thunkApi.dispatch(
          fetchLyrics({ lyricsUrl: videoMetadata.lyricsJsonUrl, isStorageActivated }),
        );
        thunkApi.dispatch(fetchSwipeColors({ colorSetting, isStorageActivated }));
      }
      return videoMetadata;
    } catch (error) {
      if (error.status === 403 || error.status === 401) {
        return thunkApi.rejectWithValue(new NotPremiumError());
      }
      throw error;
    }
  },
);

export const fetchLyrics = createAsyncThunk<Lyrics, FetchLyricsPayload>(
  `${VIDEO_PLAYER_RESOURCES_ACTION_PREFIX}/fetchLyrics`,
  async ({ lyricsUrl, isStorageActivated }): Promise<Lyrics> => {
    if (isStorageActivated) return localDB.fetchOrGetData<Lyrics>(lyricsUrl, getLyrics);
    return getLyrics(lyricsUrl);
  },
);

export const fetchSwipeColors = createAsyncThunk<SwipeColors, FetchColorPayload>(
  `${VIDEO_PLAYER_RESOURCES_ACTION_PREFIX}/fetchSwipeColors`,
  async ({ colorSetting, isStorageActivated }): Promise<SwipeColors> => {
    if (isStorageActivated) return localDB.fetchOrGetData<SwipeColors>(colorSetting, getSwipeColor);
    return getSwipeColor(colorSetting);
  },
);
